import {captureException} from '@sentry/browser';
import {css} from 'aphrodite';
import classNames from 'classnames';
import React, {Dispatch, SetStateAction, useState} from 'react';

import {styles} from 'search/blast/constants';
import {CandidateBLASTNUiParamsType} from 'search/blast/parameters/blastnConstants';
import {CandidateBLASTPUiParamsType} from 'search/blast/parameters/blastpConstants';
import {MatrixToGapCosts} from 'search/blast/parameters/common';
import {
  getCompBasedStatsUIField,
  getCullingLimitUIField,
  getEvalueUIField,
  getGapCostsUIField,
  getMatchScoreUIField,
  getMatrixUIField,
  getStrandUIField,
  getTaskUIField,
  getWordSizeUIField,
  UIFieldKeys,
} from 'search/blast/parameters/uiFieldHelpers';
import {
  CandidateBLASTUiParams,
  getCandidateBLASTUiParamsFromFilter,
  getDefaultBLASTParamsForTask,
} from 'search/blast/parameters/uiParamHelpers';
import {
  BLASTFilter,
  BLASTGapCost,
  BLASTMatchScore,
  BLASTMatrix,
  BLASTNParams,
  BLASTNTask,
  BLASTPParams,
  BLASTPTask,
  BLASTXParams,
  BLASTXTask,
  hasStrandParam,
  Operator,
  TBLASTNParams,
  TBLASTNTask,
} from 'search/SearchConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ControlledNumericInput, {
  IntermediateValueControlledNumericInput,
} from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

export interface BLASTParamsSectionPropsWithKeys extends BLASTParamsSectionProps {
  title?: string;
  keys: Array<UIFieldKeys>;
}

export interface BLASTParamsSectionProps {
  candidateParams: CandidateBLASTUiParams;
  setCandidateParams?: Dispatch<SetStateAction<CandidateBLASTUiParams>>;
  filter: BLASTFilter;
  setFilter: Dispatch<SetStateAction<BLASTFilter>>;
}

function BLASTParamsSection({
  candidateParams,
  setCandidateParams,
  filter,
  setFilter,
  title,
  keys,
}: BLASTParamsSectionPropsWithKeys): JSX.Element {
  const [isOpen, setIsOpen] = useState(title === undefined);
  // oxlint-disable-next-line array-callback-return
  const dropdowns = Object.keys(candidateParams).map((key) => {
    if (!keys.includes(key as UIFieldKeys)) {
      return;
    }

    switch (key) {
      case UIFieldKeys.TASK:
        if (filter.operator === Operator.TBLASTX) {
          throw new Error('TBLASTX should not have program type.');
        }
        return (
          <ParamsInputField
            key={key}
            label="Program"
            name={key}
            options={(candidateParams as CandidateBLASTNUiParamsType).task.values.map(
              (value: BLASTNTask | BLASTPTask | BLASTXTask | TBLASTNTask) => ({
                value,
                display: getTaskUIField(value),
              })
            )}
            currentValue={(filter.params! as BLASTNParams).task}
            updateParams={(value): void => {
              const newFilter: BLASTFilter = {
                ...filter,
                params: getDefaultBLASTParamsForTask(filter, value),
              };
              setFilter(newFilter);
              if (setCandidateParams) {
                setCandidateParams(getCandidateBLASTUiParamsFromFilter(newFilter));
              }
            }}
            wide={true}
          />
        );
      case UIFieldKeys.EVALUE:
        return (
          <ParamsInputField
            key={key}
            label={getEvalueUIField(candidateParams.evalue).label}
            name={key}
            options={[]}
            currentValue={filter.params![key]}
            updateParams={(value): void => {
              setFilter((previousFilter) => ({
                ...previousFilter,
                params: {
                  ...previousFilter.params,
                  [key]: value,
                },
              }));
            }}
          />
        );
      case UIFieldKeys.WORD_SIZE: {
        const parameterOptions = getWordSizeUIField(candidateParams.word_size);
        return (
          <ParamsInputField
            key={key}
            label={parameterOptions.label}
            name={key}
            options={parameterOptions.options}
            currentValue={filter.params!.word_size}
            updateParams={(value): void => {
              setFilter((previousFilter) => ({
                ...previousFilter,
                params: {
                  ...previousFilter.params,
                  word_size: value,
                },
              }));
            }}
          />
        );
      }
      case UIFieldKeys.STRAND: {
        const strandOptions = getStrandUIField((candidateParams as CandidateBLASTNUiParamsType).strand);
        return (
          <ParamsInputField
            key={key}
            label={strandOptions.label}
            name={key}
            options={strandOptions.options}
            currentValue={filter.params && hasStrandParam(filter.params) ? filter.params.strand : undefined}
            updateParams={(value): void => {
              setFilter((previousFilter) => ({
                ...previousFilter,
                params: {
                  ...previousFilter.params,
                  strand: value,
                } as BLASTNParams,
              }));
            }}
          />
        );
      }
      case UIFieldKeys.GAP_COST: {
        const gapCostOptions = getGapCostsUIField((candidateParams as CandidateBLASTNUiParamsType).gap_cost);
        const {gapopen, gapextend} = filter.params! as BLASTNParams;
        return (
          <ParamsInputField
            key={key}
            label={gapCostOptions.label}
            name={key}
            options={gapCostOptions.options}
            currentValue={{existence: gapopen!, extension: gapextend}}
            updateParams={(value: BLASTGapCost): void => {
              setFilter((previousFilter) => ({
                ...previousFilter,
                params: {
                  ...previousFilter.params,
                  gapopen: value.existence,
                  gapextend: value.extension,
                },
              }));
            }}
          />
        );
      }
      case UIFieldKeys.MATCH_SCORE: {
        const matchScoreOptions = getMatchScoreUIField(
          (candidateParams as CandidateBLASTNUiParamsType).match_score
        );
        const {reward, penalty} = filter.params! as BLASTNParams;
        return (
          <ParamsInputField
            key={key}
            label={matchScoreOptions.label}
            name={key}
            options={matchScoreOptions.options}
            currentValue={{reward, penalty}}
            updateParams={(value: BLASTMatchScore): void => {
              const newBLASTNParams: BLASTNParams = {
                ...(filter.params as BLASTNParams),
                reward: value.reward,
                penalty: value.penalty,
              };
              const newFilter: BLASTFilter = {
                ...filter,
                params: newBLASTNParams,
              };
              const newCandidateUiParams: CandidateBLASTNUiParamsType = getCandidateBLASTUiParamsFromFilter(
                newFilter
              ) as CandidateBLASTNUiParamsType;

              // Gap cost depends on match score. Based on the newly chosen
              // match score, the current gap cost may need to be reset.
              const existingGapCost = newCandidateUiParams.gap_cost.values.find(
                (gapCost) =>
                  gapCost.existence === newBLASTNParams.gapopen &&
                  gapCost.extension === newBLASTNParams.gapextend
              );
              if (existingGapCost === undefined) {
                newBLASTNParams.gapopen = newCandidateUiParams.gap_cost.defaultValue.existence;
                newBLASTNParams.gapextend = newCandidateUiParams.gap_cost.defaultValue.extension;
              }

              if (setCandidateParams) {
                setCandidateParams(newCandidateUiParams);
              }
              setFilter(newFilter);
            }}
          />
        );
      }
      case UIFieldKeys.CULLING_LIMIT:
        return (
          <ParamsInputField
            key={key}
            label={
              getCullingLimitUIField((candidateParams as CandidateBLASTNUiParamsType).culling_limit).label
            }
            name={key}
            options={[]}
            currentValue={filter.params!.culling_limit}
            updateParams={(value): void => {
              setFilter((previousFilter) => ({
                ...previousFilter,
                params: {
                  ...previousFilter.params,
                  culling_limit: value,
                },
              }));
            }}
          />
        );
      case UIFieldKeys.MATRIX: {
        const matrixOptions = getMatrixUIField((candidateParams as CandidateBLASTPUiParamsType).matrix);
        return (
          <ParamsInputField
            key={key}
            label={matrixOptions.label}
            name={key}
            options={matrixOptions.options}
            currentValue={(filter.params! as BLASTPParams)[key]}
            updateParams={(value): void => {
              if (filter.operator === Operator.TBLASTN) {
                // TBLASTX's matrix does not affect other parameters
                setFilter((previousFilter) => ({
                  ...previousFilter,
                  params: {
                    ...previousFilter.params,
                    matrix: value,
                  },
                }));
                return;
              }

              const newDefaultGapCost = MatrixToGapCosts[value as BLASTMatrix].defaultValue;
              const newBLASTParams: BLASTPParams | BLASTXParams | TBLASTNParams = {
                ...(filter.params as BLASTPParams | BLASTXParams | TBLASTNParams),
                matrix: value,
                gapopen: newDefaultGapCost.existence,
                gapextend: newDefaultGapCost.extension,
              };
              const newFilter: BLASTFilter = {
                ...filter,
                params: newBLASTParams,
              };
              const newCandidateUiParams: CandidateBLASTUiParams =
                getCandidateBLASTUiParamsFromFilter(newFilter);
              if (setCandidateParams) {
                setCandidateParams(newCandidateUiParams);
              }
              setFilter(newFilter);
            }}
          />
        );
      }
      case UIFieldKeys.COMP_BASED_STATS: {
        const compBasedStatsOptions = getCompBasedStatsUIField(
          (candidateParams as CandidateBLASTPUiParamsType).comp_based_stats
        );
        return (
          <ParamsInputField
            key={key}
            label={compBasedStatsOptions.label}
            name={key}
            options={compBasedStatsOptions.options}
            currentValue={(filter.params! as BLASTPParams).comp_based_stats}
            updateParams={(value): void => {
              setFilter((previousFilter) => ({
                ...previousFilter,
                params: {
                  ...previousFilter.params,
                  comp_based_stats: value,
                },
              }));
            }}
          />
        );
      }
      default:
        throw new Error(`Unsupported BLAST parameter: ${key}`);
    }
  });

  return (
    <div>
      {title && (
        <div className={css(styles.paramsDropdownTitle)}>
          <div
            className={css(styles.toggle)}
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          >
            <BenchlingIcon className={css(styles.icon)} icon={isOpen ? 'chevron-down' : 'chevron'} />
            {title}
          </div>
          <div className={css(styles.horizontal)} />
        </div>
      )}
      <div className={classNames('u-flex-display', css(styles.colPaddingOffset))}>
        <FormGroup className="u-flex-grow u-no-spacing-bottom">{isOpen && dropdowns}</FormGroup>
      </div>
    </div>
  );
}

export function ProgramTypeBLASTParamsSection({
  candidateParams,
  setCandidateParams,
  filter,
  setFilter,
}: BLASTParamsSectionProps): JSX.Element {
  return (
    <BLASTParamsSection
      candidateParams={candidateParams}
      setCandidateParams={setCandidateParams}
      filter={filter}
      setFilter={setFilter}
      keys={[UIFieldKeys.TASK]}
    />
  );
}

export function GeneralParametersBLASTParamsSection({
  candidateParams,
  filter,
  setFilter,
}: BLASTParamsSectionProps): JSX.Element {
  return (
    <BLASTParamsSection
      candidateParams={candidateParams}
      filter={filter}
      setFilter={setFilter}
      title="General parameters"
      keys={[UIFieldKeys.EVALUE, UIFieldKeys.WORD_SIZE, UIFieldKeys.STRAND, UIFieldKeys.CULLING_LIMIT]}
    />
  );
}

export function ScoringBLASTParamsSection({
  candidateParams,
  setCandidateParams,
  filter,
  setFilter,
}: BLASTParamsSectionProps): JSX.Element {
  return (
    <BLASTParamsSection
      candidateParams={candidateParams}
      setCandidateParams={setCandidateParams}
      filter={filter}
      setFilter={setFilter}
      title="Scoring"
      keys={[UIFieldKeys.MATRIX, UIFieldKeys.GAP_COST, UIFieldKeys.MATCH_SCORE, UIFieldKeys.COMP_BASED_STATS]}
    />
  );
}

interface ParamsInputFieldProps<T> {
  label: string;
  name: string;
  options: Array<{value: T; display?: string}>;
  currentValue: T;
  updateParams: (value: T) => void;
  wide?: boolean;
}

export function ParamsInputField<T>(props: ParamsInputFieldProps<T>): JSX.Element {
  const col = props.wide ? 7 : 6;
  return (
    <Col lg={col} md={col} sm={col} xs={col} className={css(styles.param)} key={props.name}>
      <FieldLabel label={props.label} />
      {props.options.length > 0 && <ParamsDropdown {...props} />}
      {props.name === UIFieldKeys.EVALUE && (
        <IntermediateValueControlledNumericInput
          value={props.currentValue as number}
          onChange={(event) => {
            props.updateParams(event.target.numericValue as T);
          }}
          required={true}
          min={0}
          minIsInclusive={true}
          dataTestAttrs={{component: 'BLASTParamsInputField', element: `${props.name}Input`}}
        />
      )}
      {props.name === UIFieldKeys.CULLING_LIMIT && (
        <ControlledNumericInput
          value={props.currentValue as number}
          onChange={(event) => {
            props.updateParams(event.target.numericValue as T);
          }}
          required={true}
          min={0}
          validateInteger={true}
          dataTestAttrs={{component: 'BLASTParamsInputField', element: `${props.name}Input`}}
        />
      )}
    </Col>
  );
}

function ParamsDropdown<T>(props: ParamsInputFieldProps<T>): JSX.Element {
  const currentOption = props.options.find(
    (option) => JSON.stringify(option.value) === JSON.stringify(props.currentValue)
  );
  if (currentOption === undefined) {
    captureException('BLAST parameter current option is undefined', {
      extra: {
        label: props.label,
        name: props.name,
        options: props.options,
        currentValue: props.currentValue,
      },
    });
  }
  const menuOptions = props.options.map((parameterOption) => (
    <Option
      onSelect={(value: T): void => {
        props.updateParams(value);
      }}
      value={parameterOption.value}
      key={JSON.stringify(parameterOption)}
    >
      {parameterOption.display ?? parameterOption.value}
    </Option>
  ));

  return (
    <SelectDropdown
      displayName={
        currentOption?.display || currentOption?.value || 'Error, please select a different option'
      }
      dataTestAttrs={{component: 'BLASTParamsInputField', element: `${props.name}Dropdown`}}
      menu={
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <Menu value={props.currentValue} scrollable={true}>
          {menuOptions}
        </Menu>
      }
    />
  );
}
